import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import { toast } from 'react-toastify';
import useBranch from '../../hooks/useBranch';

function EmployeeCardStockManagement() {
  const { getEmployeeCardStock, addEmployeeCardShipment, isLoading } = useBranch();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [saving, setSaving] = useState(false);

  const sortedBranches = useMemo(
    () => [...branches].sort((a, b) => Number(b.isLowStock) - Number(a.isLowStock)),
    [branches]
  );

  const fetchStock = async () => {
    try {
      const data = await getEmployeeCardStock();
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
    } catch (err) {
      toast.error('Fehler beim Laden der Mitarbeiterkarten-Bestände');
    }
  };

  useEffect(() => {
    fetchStock();
  }, []);

  const handleOpenShipment = branch => {
    setSelectedBranch(branch);
  };

  const handleCloseShipment = () => {
    setSelectedBranch(null);
  };

  const handleSaveShipment = async event => {
    event.preventDefault();
    if (!selectedBranch) return;

    const formData = new FormData(event.currentTarget);
    const quantity = Number.parseInt(formData.get('quantity'), 10);
    const lowStockThresholdRaw = formData.get('lowStockThreshold');
    const lowStockThreshold =
      lowStockThresholdRaw === '' ? undefined : Number.parseInt(lowStockThresholdRaw, 10);
    const note = String(formData.get('note') || '').trim();

    if (!Number.isInteger(quantity) || quantity <= 0) {
      toast.error('Bitte eine gültige Anzahl eingeben');
      return;
    }

    setSaving(true);
    try {
      await addEmployeeCardShipment(selectedBranch.branchId, {
        quantity,
        lowStockThreshold,
        note,
      });
      toast.success('Versand wurde gebucht');
      handleCloseShipment();
      await fetchStock();
    } catch (err) {
      toast.error('Fehler beim Buchen des Kartenversands');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && branches.length === 0) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ width: '100%', mt: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ width: '100%', mt: 4 }}>
      <Box>
        <Typography variant='h5'>Mitarbeiterkarten-Rohlinge</Typography>
        <Typography variant='body2' color='text.secondary'>
          Hier wird gebucht, wie viele Mitarbeiterkarten an die Standorte verschickt wurden.
          Niedrige Bestände werden hervorgehoben.
        </Typography>
      </Box>

      {branches.some(branch => branch.isLowStock) && (
        <Alert severity='warning'>
          Bei mindestens einem Standort neigen sich die Mitarbeiterkarten-Rohlinge dem Ende zu.
        </Alert>
      )}

      <Grid container spacing={2}>
        {sortedBranches.map(branch => (
          <Grid item xs={12} md={6} lg={4} key={branch.branchId}>
            <Card variant='outlined'>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <InventoryIcon color={branch.isLowStock ? 'warning' : 'primary'} />
                      <Typography variant='h6'>{branch.locationName || branch.longName}</Typography>
                    </Stack>
                    <Chip
                      color={branch.isLowStock ? 'warning' : 'success'}
                      label={`${branch.remaining} übrig`}
                    />
                  </Stack>

                  <Typography variant='body2' color='text.secondary'>
                    Versandt: {branch.shippedTotal} · Gedruckt: {branch.printedTotal} · Fehldrucke:{' '}
                    {branch.misprintTotal} · Warnschwelle: {branch.lowStockThreshold}
                  </Typography>

                  <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenShipment(branch)}
                  >
                    Versand buchen
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={!!selectedBranch} onClose={handleCloseShipment} maxWidth='sm' fullWidth>
        <Box component='form' onSubmit={handleSaveShipment}>
          <DialogTitle>Kartenversand buchen</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant='body2'>
                Standort: {selectedBranch?.locationName || selectedBranch?.longName}
              </Typography>
              <TextField
                name='quantity'
                label='Anzahl verschickter Rohlinge'
                type='number'
                inputProps={{ min: 1, step: 1 }}
                required
                fullWidth
              />
              <TextField
                name='lowStockThreshold'
                label='Warnschwelle'
                type='number'
                inputProps={{ min: 0, step: 1 }}
                defaultValue={selectedBranch?.lowStockThreshold ?? 10}
                fullWidth
              />
              <TextField name='note' label='Notiz' multiline minRows={2} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseShipment} disabled={saving}>
              Abbrechen
            </Button>
            <Button type='submit' variant='contained' disabled={saving}>
              {saving ? <CircularProgress size={18} /> : 'Buchen'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
}

export default EmployeeCardStockManagement;
